import React, { useCallback, useMemo } from 'react';
import { Button, Box, Flex, Text, Grid, GridItem, Icon } from '@televet/kibble-ui';
import partial from 'lodash-es/partial';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { addAutomationPrompt, updateAutomationErrors } from 'pages/Automations/state/automationsSlice';
import { AutomationRunActionPromptType } from 'shared/types/graphql';
import { PromptOptionDetail, promptOptionDetails } from 'pages/Automations/types/PromptOptionDetail';
import { useGetFieldErrorText } from 'pages/Automations/hooks/useGetFieldErrorText';
import useClinicUser from 'shared/hooks/useClinicUser';

interface SelectPromptTypeBlockProps {
  toggleVisiblity?: () => void;
  closeButtonVisible?: boolean;
  addPromptAtIndex: number;
  selectPromptTypeBlockCallbackRef?: (element: HTMLDivElement | null) => void;
}

export const SelectPromptTypeBlock = ({
  toggleVisiblity,
  closeButtonVisible = false,
  addPromptAtIndex,
  selectPromptTypeBlockCallbackRef,
}: SelectPromptTypeBlockProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { currentClinic } = useClinicUser();
  const promptContainerErrors = useAppSelector((state) => state.automations.errors.promptContainer);
  const errorText = useGetFieldErrorText(promptContainerErrors);
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);

  const fieldIsInvalid = useMemo(() => {
    return Boolean(promptContainerErrors.length);
  }, [promptContainerErrors]);

  const handlePromptOptionClick = useCallback(
    (promptType: AutomationRunActionPromptType) => {
      dispatch(addAutomationPrompt({ promptType, atIndex: addPromptAtIndex }));

      if (toggleVisiblity) {
        toggleVisiblity();
      }

      if (fieldIsInvalid) {
        dispatch(updateAutomationErrors({ promptContainer: [] }));
      }
    },
    [toggleVisiblity, dispatch, fieldIsInvalid, addPromptAtIndex],
  );

  // Do not show Payment prompt option if clinic does not have an active Stripe account
  const promptOptions = useMemo(() => {
    const hidePaymentPrompt = !(currentClinic?.submerchantStripeId && currentClinic?.submerchantStripeChargesEnabled);
    const hideFormPrompt = isGlobalEditor;
    return Object.values(promptOptionDetails).filter((promptOption) => {
      if (promptOption.type === AutomationRunActionPromptType.Form && hideFormPrompt) {
        return false;
      }
      return !(promptOption.type === AutomationRunActionPromptType.Payment && hidePaymentPrompt);
    });
  }, [currentClinic, isGlobalEditor]);

  return (
    <Box
      bg="background.subtle"
      boxShadow={fieldIsInvalid ? `0 0 0 2px var(--chakra-colors-border-danger)` : ''}
      border="1px"
      borderColor="border.default"
      borderRadius={8}
      mt={8}
      className="Automations__SelectPromptTypeBlock"
      ref={selectPromptTypeBlockCallbackRef}
      scrollMarginTop="150px"
    >
      <Flex
        className="PromptsOptionBlock__Header"
        py={2}
        pl={4}
        pr={2}
        justify="space-between"
        align="center"
        minH="56px"
      >
        <Text size="lg" fontWeight="bold">
          Add Prompt
        </Text>
        {closeButtonVisible && <Button variant="ghostNeutral" iconName="close" onClick={toggleVisiblity} />}
      </Flex>
      <Grid templateColumns="repeat(2, 1fr)" gap={3} borderRadius={2} px={4} pb={6}>
        {promptOptions.map((details: PromptOptionDetail) => (
          <GridItem key={details.type} cursor="pointer">
            <Flex
              className="PromptsOptionBlock__Option"
              bg="background.default"
              h="100%"
              onClick={partial(handlePromptOptionClick, details.type)}
              data-testid={`prompt-option-${details.type}`}
              p={3}
              borderRadius="md"
              align="flex-start"
              border="3px solid transparent"
              _hover={{
                borderColor: 'border.default',
              }}
            >
              <Box>
                <Icon size="xl" name={details.iconName} />
              </Box>
              <Box pt={1} pl={2}>
                <Text fontWeight="bold" as="p">
                  {details.title}
                </Text>
                <Text as="p" size="sm" variant="subtle">
                  {details.description}
                </Text>
              </Box>
            </Flex>
          </GridItem>
        ))}
      </Grid>
      {fieldIsInvalid && (
        <Text mx={4} mb={2} mt="-8px" size="xs" as="p" variant="danger">
          {errorText}
        </Text>
      )}
    </Box>
  );
};
